/**
 * CadenceSectionPure
 *
 * Pure presentational component for rendering cadence steps.
 * Works in both Next.js (with tRPC) and Electron (with IPC) by receiving
 * all data and callbacks via props.
 */
import type { ReactNode } from 'react';
import { useState, useMemo } from 'react';

import {
  CGStepPure,
  daysToMs,
  type EditStepPure,
  type CreateStepData,
  type CadenceGraphNodesPure,
  type CadenceStepTypePure,
  type StepUpdateInputPure,
  type SiftConfig,
} from '../CGStep';
import { CampaignNewDayPickerPure } from '../CampaignDayCard/CampaignNewDayPickerPure';
import type { ReminderAction } from '../ManualSelect/ManualSelectPure';
import type { StepData, StepType } from '../CGStep/CTSContentEditingPure';
import { UserAtVariables, ContactAtVariables } from '@poseidon/types/types/atVariables.type';
import type { TipTapVariable, TipTapDocument } from '../Input2/TipTapInput';

const USER_AT_VARS_SET = new Set<string>(UserAtVariables);
const CONTACT_AT_VARS_SET = new Set<string>(ContactAtVariables);

export type AIMessageTeamLogo = {
  url: string;
  webp?: string;
} | null;

// ============================================================================
// Types
// ============================================================================

export type CadenceStepState = 'not-started' | 'interrupted' | 'skipped' | 'finished' | 'canceled' | 'action-required' | 'responded' | 'contact_unavailable';

export type CadenceGraph = {
  startId: string | null;
  nodes: CadenceGraphNodesPure;
};

export type CadenceData = {
  id: string;
  templateId?: string | null;
  createdOn: Date;
};

export type TemplateInfo = {
  goal?: string | null;
  previewText?: string | null;
  recentPosts?: string[];
};

export type ContactInfo = {
  id: string;
  name?: string;
  company?: string;
  title?: string;
  headline?: string;
  about?: string;
  city?: string;
  /**
   * True when the auth user is already a LinkedIn connection of this contact.
   * Drives the "Connect" picker option's disabled state in the cadence card.
   */
  alreadyConnectedOnLinkedIn?: boolean;
};

export type DocumentData = {
  id: string;
  title?: string;
  url?: string;
  imageUrl?: string;
};

export type StepCreateInput = {
  type: string;
  body2?: string | null;
  subject?: string | null;
  delay?: number;
  afterStepId?: string | null;
  option?: 'one' | 'two';
  cc?: string;
  bcc?: string;
  onlyOpenLink?: boolean;
  postsToLike?: number;
  siftConditions?: SiftConfig;
};

export type StepUpdateInput = StepUpdateInputPure;

export type AIMessageSectionData = {
  isSuperAdmin: boolean;
  canEditTeamData: boolean;
  teamName: string;
  teamLogo?: AIMessageTeamLogo;
  userProfilePicture?: string | null;
  userName?: string | null;
  takeActionTemplate: string | null;
  teamAiInfo: string | null;
  workContext: string | null;
  isLoading?: boolean;
};

// ============================================================================
// Component Props
// ============================================================================

export type CadenceSectionPureProps = {
  // Data
  cadence: CadenceData;
  stepsGraph: CadenceGraph | null;
  templateInfo?: TemplateInfo | null;
  documents?: DocumentData[];
  contactInfo: ContactInfo;
  atVariables: Record<string, string>;

  // Callbacks. All optional — when ANY is missing the section renders in
  // read-only mode: the step plan still displays, but every edit
  // affordance (add-step pickers, click-to-edit rows, delay pills,
  // mark-done, delete) is hidden. Hosts that can't wire the step
  // mutations (e.g. the extension sidepanel) simply omit them.
  onCreateStep?: (data: StepCreateInput) => unknown;
  onUpdateStep?: (stepId: string, data: StepUpdateInput) => unknown;
  onDeleteStep?: (stepId: string) => unknown;
  onChangeStepType?: (stepId: string, type: string) => unknown;
  onMarkStepFinished?: (stepId: string) => unknown;
  onToggleInmailOpen?: (nodeId: string, onlyOpenLink: boolean) => unknown;

  // Render props for platform-specific components
  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => ReactNode;

  // Reminder editor — list of available reminder actions for the team and a
  // create-on-the-fly callback. When provided, the inline reminder editor
  // shows them in the dropdown and lets the user type a new name to add.
  reminderActions?: { id: string; name: string }[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;

  // Optional
  className?: string;
  voiceEnabled?: boolean;
  voiceCallingEnabled?: boolean;
  /**
   * Origin prefixed to the "See in template" link (e.g.
   * `https://app.useposeidon.com`). The dashboard omits it (relative
   * link); the extension/electron must pass it — a relative href inside
   * a non-dashboard origin resolves to a broken URL.
   */
  templateLinkBaseUrl?: string;
};

// ============================================================================
// Component
// ============================================================================

export function CadenceSectionPure({
  cadence,
  stepsGraph,
  templateInfo: _templateInfo,
  documents,
  contactInfo,
  atVariables,
  onCreateStep,
  onUpdateStep,
  onDeleteStep,
  onChangeStepType,
  onMarkStepFinished,
  onToggleInmailOpen,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  className,
  voiceEnabled,
  voiceCallingEnabled,
  templateLinkBaseUrl,
}: CadenceSectionPureProps) {
  const [editingStep, setEditingStep] = useState<EditStepPure | undefined>();

  const hasSteps = stepsGraph?.nodes != null && stepsGraph?.startId != null;

  // Read-only when any step mutation is missing — display the plan,
  // hide all edit affordances. All-or-nothing mirrors the previous
  // host-side gate that required every callback to render at all.
  const readOnly =
    onCreateStep == null ||
    onUpdateStep == null ||
    onDeleteStep == null ||
    onChangeStepType == null ||
    onMarkStepFinished == null ||
    onToggleInmailOpen == null;

  // Convert atVariables to TipTapVariable[]. Group by whether the key is a
  // known user-scoped variable (My*, Today) vs a contact-scoped variable so
  // the TipTap mention dropdown can split them under "My Variables" /
  // "Contact Variables" headers.
  const tipTapVariables = useMemo<TipTapVariable[]>(() => {
    return Object.entries(atVariables).map(([key, value]) => {
      let group: 'user' | 'contact' | undefined;
      if (USER_AT_VARS_SET.has(key) || key === 'Today') group = 'user';
      else if (CONTACT_AT_VARS_SET.has(key)) group = 'contact';
      else group = 'contact';
      // Chip display text: the resolved VALUE for the current contact
      // (e.g. "Michael") when one exists, falling back to "@FirstName"
      // when the variable has no resolution yet (template editor / new
      // contact). The mention chip is already styled blue + underlined
      // via the `.mention` class (see TipTapInput.tsx editor styles),
      // so the user sees "Michael" in blue and knows it'll be
      // re-resolved per contact at send time. Variable identity is
      // preserved via `data-id` regardless of what's in `data-label`.
      return {
        id: key,
        label: value || `@${key}`,
        group,
      };
    });
  }, [atVariables]);

  // Convert documents to TipTapDocument[]
  const tipTapDocuments = useMemo<TipTapDocument[]>(() => {
    return (documents ?? []).map((doc) => ({
      id: doc.id,
      label: doc.title ?? 'Untitled',
      imageUrl: doc.imageUrl,
    }));
  }, [documents]);

  // Handle create step - convert CreateStepData to StepCreateInput
  const handleCreateStep = async (data: CreateStepData) => {
    await onCreateStep?.({
      type: data.type,
      body2: data.body2,
      subject: data.subject,
      delay: data.delay,
      afterStepId: data.afterStepId,
      option: data.option,
      cc: data.cc,
      bcc: data.bcc,
      onlyOpenLink: data.onlyOpenLink,
      postsToLike: data.postsToLike,
      siftConditions: data.siftConditions,
    });
  };

  // Read-only with nothing to show — skip the section entirely rather
  // than rendering an empty timeline shell.
  if (readOnly && !hasSteps) return null;

  return (
    <div className={className}>
      <div className="flex flex-col" id="scheduled_divider">
        <div className="flex flex-col">

          {/* "Add a step at the start of the cadence" affordance.
              Renders for both empty and populated cadences — afterStepId
              is undefined so the new step lands BEFORE any existing
              first step. Same component / chrome as the trailing
              new-day trigger so the start and end of the timeline read
              consistently. Hidden in read-only mode. */}
          {!readOnly && renderFirstAddStep({
            editingStep,
            setEditingStep,
            variables: tipTapVariables,
            documents: tipTapDocuments,
            renderSiftConditionBuilder,
            reminderActions,
            reminderActionsLoading,
            onCreateReminder,
            reminderCreating,
            onReminderError,
            alreadyConnectedToContact: contactInfo.alreadyConnectedOnLinkedIn,
            voiceEnabled,
            voiceCallingEnabled,
            onCreateStep: handleCreateStep,
          })}

          {/* Steps list */}
          {hasSteps && (
            <>
              {/* Render the step tree */}
              <CGStepPure
                nodeId={stepsGraph.startId!}
                nodes={stepsGraph.nodes}
                first
                msUpToNow={cadence.createdOn?.valueOf() ?? Date.now()}
                alreadyConnectedToContact={contactInfo.alreadyConnectedOnLinkedIn}
                templateHref={
                  cadence.templateId != null
                    ? `${templateLinkBaseUrl ?? ''}/templates/${cadence.templateId}`
                    : undefined
                }
                reminderActions={reminderActions}
                reminderActionsLoading={reminderActionsLoading}
                onCreateReminder={onCreateReminder}
                reminderCreating={reminderCreating}
                onReminderError={onReminderError}
                editingStep={editingStep}
                setEditingStep={setEditingStep}
                variables={tipTapVariables}
                documents={tipTapDocuments}
                contactId={contactInfo.id}
                // CGStepPure's callbacks are required; in read-only mode
                // they're unreachable (every affordance is hidden), so
                // no-ops keep the contract without changing behavior.
                onChangeType={onChangeStepType ?? (() => {})}
                onDeleteNode={onDeleteStep ?? (() => {})}
                onCreateStep={handleCreateStep}
                onUpdateStep={onUpdateStep ?? (() => {})}
                onToggleInmailOpen={onToggleInmailOpen ?? (() => {})}
                markFinished={onMarkStepFinished ?? (() => {})}
                renderSiftConditionBuilder={renderSiftConditionBuilder}
                voiceEnabled={voiceEnabled}
                voiceCallingEnabled={voiceCallingEnabled}
                readOnly={readOnly}
              />
            </>
          )}
        </div>
        <div className="h-20" />
      </div>
    </div>
  );
}

/**
 * Renders the "add a step at the start of the cadence" affordance using
 * the same `CampaignNewDayPickerPure` chrome as the trailing new-day
 * trigger. Lifted out of the JSX so the prop wiring isn't repeated for
 * the empty-vs-populated branches.
 */
function renderFirstAddStep({
  editingStep,
  setEditingStep,
  variables,
  documents,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  alreadyConnectedToContact,
  voiceEnabled,
  voiceCallingEnabled,
  onCreateStep,
}: {
  editingStep: EditStepPure | undefined;
  setEditingStep: (value: EditStepPure | undefined) => void;
  variables: TipTapVariable[] | undefined;
  documents: TipTapDocument[] | undefined;
  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => ReactNode;
  reminderActions?: ReminderAction[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;
  alreadyConnectedToContact?: boolean;
  voiceEnabled?: boolean;
  voiceCallingEnabled?: boolean;
  onCreateStep: (data: CreateStepData) => Promise<void>;
}) {
  const FIRST_DELAY_MS = daysToMs(2);
  const isHere =
    editingStep?.type === 'add' &&
    editingStep.intent === 'new-day' &&
    editingStep.afterStepId === undefined;
  const isPickingHere = isHere && editingStep.data == null;
  const addingHere = isHere && editingStep.data != null
    ? (editingStep.data.type as CadenceStepTypePure)
    : undefined;

  return (
    <div className="mx-2 flex flex-col items-stretch">
      <CampaignNewDayPickerPure
        defaultDelayDays={2}
        // First-add picker — nothing above (start of the cadence).
        // The host adds its own spine BELOW (cadence-first-add-spine)
        // so we suppress the picker's bottom spine too to avoid
        // doubling up.
        hasNeighborAbove={false}
        hasNeighborBelow={false}
        isPickingStepType={isPickingHere}
        addingStepType={addingHere}
        existingTypes={[]}
        disabledStepReasons={
          alreadyConnectedToContact
            ? { connect: 'You are already connected to this contact.' }
            : undefined
        }
        variables={variables}
        documents={documents}
        renderSiftConditionBuilder={renderSiftConditionBuilder}
        reminderActions={reminderActions}
        reminderActionsLoading={reminderActionsLoading}
        onCreateReminder={onCreateReminder}
        reminderCreating={reminderCreating}
        onReminderError={onReminderError}
        voiceEnabled={voiceEnabled}
        voiceCallingEnabled={voiceCallingEnabled}
        onAddStep={() =>
          setEditingStep({
            type: 'add',
            option: 'one',
            intent: 'new-day',
            afterStepId: undefined,
          })
        }
        onCancelPickStepType={() => setEditingStep(undefined)}
        onPickStepType={(type) => {
          if (type === 'follow' || type === 'likeRecentPosts' || type === 'visit') {
            void onCreateStep({
              type: type as StepType,
              delay: FIRST_DELAY_MS,
              afterStepId: undefined,
              ...(type === 'likeRecentPosts' && { postsToLike: 1 }),
            });
            setEditingStep(undefined);
            return;
          }
          setEditingStep({
            type: 'add',
            option: 'one',
            intent: 'new-day',
            afterStepId: undefined,
            data: { id: 'pending', type: type as StepType, delay: FIRST_DELAY_MS },
          });
        }}
        onSaveNewStep={async (type, data: StepData) => {
          const create: CreateStepData = {
            type: type as StepType,
            delay: FIRST_DELAY_MS,
            afterStepId: undefined,
          };
          if (typeof data.subject === 'string') create.subject = data.subject;
          if (typeof data.body === 'string') create.body2 = data.body;
          if (typeof data.postsToLike === 'number') create.postsToLike = data.postsToLike;
          if (typeof data.cc === 'string') create.cc = data.cc;
          if (typeof data.bcc === 'string') create.bcc = data.bcc;
          if (data.siftConditions != null) create.siftConditions = data.siftConditions;
          if (type === 'inmail') create.onlyOpenLink = data.onlyOpenLink ?? false;
          await onCreateStep(create);
          setEditingStep(undefined);
        }}
        onCancelAddStep={() => {
          setEditingStep({
            type: 'add',
            option: 'one',
            intent: 'new-day',
            afterStepId: undefined,
          });
        }}
      />
      {/* Centered vertical spine linking this trigger / picker down to
          the first day card, matching the spine that threads through
          inter-day wait bands. The negative top margin closes the gap
          left by the trigger button's `my-3` so the line starts flush
          against the bottom of the button. */}
      <span
        aria-hidden
        data-testid="cadence-first-add-spine"
        className="mx-auto -mt-3 block h-3 w-px bg-lynch-200 dark:bg-slate-700"
      />
    </div>
  );
}
